import { useState, useCallback, useEffect } from "react";
import {
  Users,
  Calendar,
  ArrowRight,
  ShieldCheck,
  Plus,
  TrendingUp,
  TrendingDown,
  IndianRupee,
} from "lucide-react";
import {
  Row,
  Col,
  Card,
  Button,
  Statistic,
  List,
  Tag,
  Typography,
  Space,
  Segmented,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import PageLoader from "Components/PageLoader/PageLoader";
import { getVendorOverview } from "services/dashboard.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import useAuthStore from "stores/AuthStore/AuthStore";
import { LinkOnlyBadge } from "pages/Events/commons/events.helpers";

const { Title, Text } = Typography;

const PERIOD_OPTIONS = [
  { label: "7 Days", value: "7d" },
  { label: "30 Days", value: "30d" },
  { label: "90 Days", value: "90d" },
  { label: "12 Months", value: "12m" },
];

function DashboardHome() {
  const { isRootUser } = useAuthStore();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7d");

  const loadData = useCallback(
    async ({ showLoading = false } = {}) => {
      if (showLoading) setLoading(true);
      const { data: result, errors } = await getVendorOverview({ period });
      if (errors?.length > 0) {
        renderErrorNotifications(errors);
        setLoading(false);
        return;
      }
      setData(result);
      setLoading(false);
    },
    [period]
  );

  useEffect(() => {
    loadData({ showLoading: true });
  }, [loadData]);

  if (loading)
    return <PageLoader showCards={false} showHero={false} showTabs={false} />;

  const summary = data?.summary ?? {};
  const trends = data?.trends ?? {};

  const eventsData = summary.events ?? {};
  const membersData = summary.members ?? {};
  const ordersData = summary.orders ?? {};

  const revenueGrowthPct = ordersData.revenue_growth_pct;
  const memberGrowthPct = membersData.growth_30d_pct;
  const metricCards = [
    {
      title: "Total Events",
      value: eventsData.total,
      suffix: `${eventsData.this_month ?? 0} this month`,
      icon: <Calendar size={18} />,
    },
    {
      title: "Gross Revenue",
      value: ordersData.gross_revenue,
      prefix: "₹",
      precision: 2,
      suffix: `₹${(ordersData.revenue_current_month ?? 0).toLocaleString()} this month`,
      icon: <IndianRupee size={18} />,
      change: revenueGrowthPct,
    },
  ];

  if (isRootUser) {
    metricCards.push({
      title: "All-time Sign-ups",
      value: membersData.total,
      suffix: `${membersData.active ?? 0} active now`,
      icon: <Users size={18} />,
      change: memberGrowthPct,
    });
    metricCards.push({
      title: "Active Subscriptions",
      value: membersData.active_subscriptions,
      suffix: `${membersData.active_tiers ?? 0} active tiers`,
      icon: <ShieldCheck size={18} />,
    });
  }

  const revenueTrend = (trends.revenue?.trend ?? []).map((t) => ({
    month: t.month,
    revenue: (t.events_revenue ?? 0) + (t.membership_revenue ?? 0),
  }));

  return (
    <Row gutter={[16, 24]}>
      {/* Page Header */}
      <Col xs={24}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              Overview
            </Title>
            <Text type="secondary">
              Welcome back! Here&apos;s what&apos;s happening.
            </Text>
          </Col>
          <Col>
            <Space wrap>
              <Segmented
                options={PERIOD_OPTIONS}
                value={period}
                onChange={setPeriod}
              />
              <Button type="primary" icon={<Plus size={14} />}>
                <Link to="/shoup-community/events/create">Create Event</Link>
              </Button>
            </Space>
          </Col>
        </Row>
      </Col>

      {/* Metric Cards */}
      {metricCards.map((card, i) => (
        <Col key={i} xs={24} sm={12} lg={isRootUser ? 6 : 12}>
          <Card size="small" style={{ height: "100%" }}>
            <Row justify="space-between" align="top">
              <Col>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  {card.title}
                </Text>
              </Col>
              {card.icon && <Col>{card.icon}</Col>}
            </Row>
            <Statistic
              value={card.value}
              prefix={card.prefix}
              precision={card.precision}
              style={{ marginTop: 8 }}
            />
            <Space size={8} style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {card.suffix}
              </Text>
              {card.change !== null && card.change !== undefined && (
                <Text
                  style={{
                    fontSize: 12,
                    color: card.change >= 0 ? "#52c41a" : "#ff4d4f",
                  }}
                >
                  {card.change >= 0 ? (
                    <TrendingUp size={12} style={{ marginRight: 2 }} />
                  ) : (
                    <TrendingDown size={12} style={{ marginRight: 2 }} />
                  )}
                  {card.change >= 0 ? "+" : ""}
                  {card.change?.toFixed(1)}%
                </Text>
              )}
            </Space>
          </Card>
        </Col>
      ))}

      {/* Revenue Chart */}
      <Col xs={24} lg={16}>
        <Card
          title={
            <div>
              <Text strong>Revenue Growth</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {isRootUser
                  ? "Combined events + membership revenue"
                  : "Events revenue"}
              </Text>
            </div>
          }
        >
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart
              data={revenueTrend}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.5} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${v}`} />
              <Tooltip
                contentStyle={{ borderRadius: 8, fontSize: 12 }}
                formatter={(value) => [`₹${value.toLocaleString()}`, "Revenue"]}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#6366f1"
                strokeWidth={2.5}
                fill="url(#colorRevenue)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </Col>

      {/* Upcoming Events */}
      <Col xs={24} lg={8}>
        <Card
          title={<Text strong>Upcoming Events</Text>}
          extra={
            <Button type="link" size="small">
              <Link to="/shoup-community/events/list">
                <Space size={4}>
                  View all <ArrowRight size={12} />
                </Space>
              </Link>
            </Button>
          }
          style={{ height: "100%" }}
        >
          <List
            dataSource={eventsData.upcoming ?? []}
            locale={{
              emptyText: <Text type="secondary">No upcoming events.</Text>,
            }}
            renderItem={(evt) => (
              <List.Item
                style={{ padding: "8px 0", cursor: "pointer" }}
                onClick={() =>
                  navigate(`/shoup-community/events/${evt.event_id}`)
                }
              >
                <Row
                  gutter={[8, 0]}
                  align="middle"
                  wrap={false}
                  style={{ width: "100%" }}
                >
                  <Col flex="none">
                    <Calendar size={20} />
                  </Col>
                  <Col flex="auto">
                    <Text
                      strong
                      style={{ fontSize: 13, display: "block" }}
                      ellipsis
                    >
                      {evt.title}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {evt.start_date_time &&
                        format(
                          new Date(evt.start_date_time),
                          "EEE, MMM d \u2022 h:mm a"
                        )}
                    </Text>
                  </Col>
                  <Col flex="none">
                    {evt.show_in_listings === false ? (
                      <Space size={4}>
                        <Tag color="purple">{evt.publication_status}</Tag>
                        {/* No tooltip: the whole list item is a click target. */}
                        <LinkOnlyBadge withTooltip={false} />
                      </Space>
                    ) : (
                      <Tag color="purple">{evt.publication_status}</Tag>
                    )}
                  </Col>
                </Row>
              </List.Item>
            )}
          />
          <Button block style={{ marginTop: 8 }}>
            <Link to="/shoup-community/events/list">
              <Space size={4}>
                Manage Events <ArrowRight size={12} />
              </Space>
            </Link>
          </Button>
        </Card>
      </Col>

      {/* Top Events by Revenue */}
      {(ordersData.top_events ?? []).length > 0 && (
        <Col xs={24}>
          <Card title={<Text strong>Top Events by Revenue</Text>}>
            <List
              dataSource={ordersData.top_events}
              renderItem={(evt, i) => (
                <List.Item>
                  <Row
                    style={{ width: "100%" }}
                    justify="space-between"
                    align="middle"
                  >
                    <Col>
                      <Space>
                        <Tag
                          color={
                            i === 0 ? "gold" : i === 1 ? "default" : "orange"
                          }
                          style={{ fontWeight: 700 }}
                        >
                          #{i + 1}
                        </Tag>
                        <Text strong>{evt.title ?? evt.entity_id}</Text>
                      </Space>
                    </Col>
                    <Col>
                      <Space size={16}>
                        <Text type="secondary">{evt.tickets_sold} tickets</Text>
                        <Text strong>₹{evt.revenue?.toLocaleString()}</Text>
                      </Space>
                    </Col>
                  </Row>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      )}
    </Row>
  );
}

export default DashboardHome;
